import type { CSSProperties, JSX, ReactNode } from 'react';
import type { Palette } from '../_theme/palette.js';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';

interface ButtonProps {
  palette: Palette;
  variant?: ButtonVariant;
  onClick?: () => void;
  disabled?: boolean;
  ariaLabel?: string;
  fullWidth?: boolean;
  type?: 'button' | 'submit';
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  children?: ReactNode;
  style?: CSSProperties;
}

export function Button({
  palette,
  variant = 'primary',
  onClick,
  disabled,
  ariaLabel,
  fullWidth,
  type = 'button',
  leadingIcon,
  trailingIcon,
  children,
  style,
}: ButtonProps): JSX.Element {
  const variantStyle = resolveVariant(variant, palette);
  const merged: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '10px 20px',
    borderRadius: palette.radius,
    fontSize: 14,
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    transition: 'opacity 120ms ease, background 160ms ease',
    width: fullWidth ? '100%' : undefined,
    ...variantStyle,
    ...style,
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className="chirp-btn"
      style={merged}
    >
      {leadingIcon}
      {children}
      {trailingIcon}
    </button>
  );
}

function resolveVariant(variant: ButtonVariant, palette: Palette): CSSProperties {
  if (variant === 'primary') {
    return {
      border: 'none',
      background: palette.primary,
      color: palette.onPrimary,
    };
  }
  if (variant === 'secondary') {
    return {
      border: `1px solid ${palette.border}`,
      background: 'transparent',
      color: palette.text,
    };
  }
  return {
    border: 'none',
    background: 'transparent',
    color: palette.text,
  };
}
